import type { AdminOverview, Room, RoomAttention } from '@brush/api-client';
import { NotCollected, Ratio, StatCard, cohortCard } from '@brush/ui';
import type { ReactNode } from 'react';
import { useAdminOverview, usePlayerCount, useRooms, useTournamentCount } from '../../queries';
import {
  ROOMS_NEEDING_ATTENTION_INTRO,
  activePlayersLine,
  adoptionCard,
  featurePlayersCard,
  enabledRatio,
  engagementCard,
  sessionsCard,
  formatDay,
  lastActivityText,
  operatorDaysLine,
  orderedFeatures,
  volumeState,
  weekLabel,
  writerLine,
  type FeatureEntry,
} from '../../overview/overview';
import { removalHealthCard } from '../../activity/signInRemovals';
import { AreaGate } from './AreaGate';

/**
 * The admin Overview (ADR 0012 Decision 16): where to invest and which rooms need attention, read from the
 * activity rollups, the stored period metrics and the product tables. A card whose figure is not measured
 * yet says so — never a zero.
 */
export function OverviewArea() {
  const rooms = useRooms();
  const players = usePlayerCount();
  const tournaments = useTournamentCount();
  const overview = useAdminOverview();

  return (
    <AreaGate
      queries={[rooms, players, tournaments, overview]}
      loadingLabel="Loading platform…"
      errorMessage="Couldn't load the platform data."
    >
      <div className="space-y-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Total rooms" value={rooms.data?.length ?? 0} />
          <StatCard
            label="Active rooms"
            value={(rooms.data ?? []).filter((r) => r.status === 'active').length}
            hint={`of ${rooms.data?.length ?? 0} total`}
          />
          <StatCard label="Total players" value={(players.data?.total ?? 0).toLocaleString()} />
          <StatCard label="Tournaments" value={(tournaments.data ?? 0).toLocaleString()} />
        </div>

        {overview.data ? (
          <>
            <FeatureInvestment overview={overview.data} rooms={rooms.data ?? []} />
            <RoomsNeedingAttention overview={overview.data} />
            <PlatformHealth overview={overview.data} />
          </>
        ) : null}
      </div>
    </AreaGate>
  );
}

function Section({ title, intro, children }: { title: string; intro: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <header>
        <h2 className="font-display text-lg font-semibold text-ink-950">{title}</h2>
        <p className="mt-1 max-w-3xl text-xs text-ink-800/70">{intro}</p>
      </header>
      {children}
    </section>
  );
}

function FeatureInvestment({ overview, rooms }: { overview: AdminOverview; rooms: Room[] }) {
  return (
    <Section
      title="Feature investment"
      intro={
        <>
          Recorded activity by feature, week by week. Operator side is room staff; player side is
          players, signed in with an account or not yet. Administrators and internal rooms are left
          out.{' '}
          {overview.collectedSince
            ? `Counting began ${formatDay(overview.collectedSince)}; earlier weeks are not shown.`
            : 'Nothing has been counted on this environment yet.'}
        </>
      }
    >
      <div className="grid gap-4 lg:grid-cols-2">
        {orderedFeatures(overview.features).map((entry) => (
          <FeatureCard
            key={entry.feature}
            entry={entry}
            rooms={rooms}
            adoption={overview.adoption}
            players={overview.featurePlayers}
          />
        ))}
      </div>
      <CohortRetentionCard overview={overview} />
    </Section>
  );
}

function FeatureCard({
  entry,
  rooms,
  adoption,
  players,
}: {
  entry: FeatureEntry & { activity: AdminOverview['features'][number] };
  rooms: Room[];
  adoption: AdminOverview['adoption'];
  players: AdminOverview['featurePlayers'];
}) {
  const state = volumeState(entry.activity);
  const distinct = featurePlayersCard(entry.feature, entry.label, players);
  const enabled = entry.switchKey ? enabledRatio(rooms, entry.switchKey) : null;
  const adopted = entry.switchKey ? adoptionCard(entry.feature, adoption) : null;

  return (
    <article className="space-y-3 border border-line bg-surface p-4">
      <h3 className="font-display font-semibold text-ink-950">{entry.label}</h3>

      {enabled ? (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">
            Rooms with it switched on
          </div>
          <div className="mt-1.5">
            <Ratio numerator={enabled.numerator} denominator={enabled.denominator} />
          </div>
          <p className="mt-1 text-[11px] text-ink-800/60">Internal rooms are not counted.</p>
        </div>
      ) : null}

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">Activity volume</div>
        {state.kind === 'not-collected' ? (
          <p className="mt-1.5 text-xs text-ink-800">
            Not collected yet — nothing records {entry.label.toLowerCase()} activity yet.
          </p>
        ) : state.kind === 'nothing-counted' ? (
          <p className="mt-1.5 text-xs text-ink-800">Collected, but nothing has been counted on this environment yet.</p>
        ) : (
          <>
            <table className="mt-1.5 w-full text-xs">
              <thead>
                <tr className="text-left text-ink-800/60">
                  <th className="py-1 font-medium">Week</th>
                  <th className="py-1 text-right font-medium">Operator side</th>
                  <th className="py-1 text-right font-medium">Player side</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {[...state.weeks].reverse().map((w) => (
                  <tr key={w.weekStart}>
                    <td className="py-1 text-ink-950">{weekLabel(w)}</td>
                    <td className="num py-1 text-right font-mono">{w.operatorSide.toLocaleString()}</td>
                    <td className="num py-1 text-right font-mono">{w.playerSide.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-1.5 text-[11px] text-ink-800/60">
              Counts: {entry.activity.collected.map((c) => c.description.replace(/\.$/, '')).join('; ')}.
            </p>
          </>
        )}
      </div>

      {adopted ? (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">
            Adoption breadth{adopted.kind === 'measured' ? ` · ${adopted.month}` : ''}
          </div>
          {adopted.kind === 'not-measured' ? (
            <p className="mt-1.5 text-xs text-ink-800">{adopted.says}</p>
          ) : (
            <>
              <div className="mt-1.5">
                <Ratio numerator={adopted.numerator} denominator={adopted.denominator} />
              </div>
              <p className="mt-1 text-[11px] text-ink-800/60">
                Rooms with it on every day of the month that used it, out of those rooms. Internal rooms are
                not counted.
              </p>
              <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">
                Switched on but idle
              </div>
              {adopted.idle.length === 0 ? (
                <p className="mt-1 text-xs text-ink-800">None — every room with it on used it.</p>
              ) : (
                <ul className="mt-1 text-xs text-ink-950">
                  {adopted.idle.map((name, i) => (
                    <li key={`${name}-${i}`}>{name}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      ) : null}

      {distinct ? (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">{distinct.title}</div>
          <div className="mt-1.5 text-sm text-ink-950">{distinct.soFar}</div>
          <div className="text-xs text-ink-800/70">{distinct.lastMonth}</div>
          <p className="mt-1 text-[11px] text-ink-800/60">
            A player counts once however many rooms they used it at. Staff and internal rooms are not counted.
          </p>
        </div>
      ) : null}
    </article>
  );
}

/**
 * Platform cohort retention (step 19): of the players first seen in each month, how many were active in
 * each month since — both counts in every cell. Everybody first seen before the grid began is one row. A
 * cohort's months before it joined are blank: nobody could have returned yet.
 */
function CohortRetentionCard({ overview }: { overview: AdminOverview }) {
  const card = cohortCard(overview.cohort ?? null);
  return (
    <article className="space-y-3 border border-line bg-surface p-4">
      <h3 className="font-display font-semibold text-ink-950">Platform cohort retention</h3>
      {card.kind === 'not-measured' ? (
        <p className="text-xs text-ink-800">{card.says}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-ink-800/60">
                  <th className="py-1 pr-4 font-medium">First seen</th>
                  {card.months.map((m) => (
                    <th key={m} className="py-1 pr-4 font-medium">
                      Active in {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {card.rows.map((row) => (
                  <tr key={row.label}>
                    <td className="py-1.5 pr-4 text-ink-950">{row.label}</td>
                    {row.cells.map((cell, i) => (
                      <td key={card.months[i]} className="min-w-[8rem] py-1.5 pr-4">
                        {cell ? <Ratio numerator={cell.players} denominator={cell.size} /> : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-ink-800/60">
            A player belongs to the month they were first seen at any room. Staff and internal rooms are not
            counted. Up to six months, ending with the last one every room has closed.
          </p>
        </>
      )}
    </article>
  );
}

function RoomsNeedingAttention({ overview }: { overview: AdminOverview }) {
  return (
    <Section
      title="Rooms needing attention"
      intro={ROOMS_NEEDING_ATTENTION_INTRO}
    >
      <div className="overflow-x-auto border border-line bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs text-ink-800/70">
              <th className="px-4 py-2 font-medium">Room</th>
              <th className="px-4 py-2 font-medium">Operator-days</th>
              <th className="px-4 py-2 font-medium">Active players</th>
              <th className="px-4 py-2 font-medium">Last operator activity</th>
              <th className="px-4 py-2 text-right font-medium">Tournaments awaiting results</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {overview.rooms.map((room: RoomAttention) => (
              <tr key={room.roomId}>
                <td className="px-4 py-2 text-ink-950">{room.name}</td>
                <td className="px-4 py-2 text-ink-800">
                  {(() => {
                    const line = operatorDaysLine(room);
                    return (
                      <>
                        <div className="text-ink-950">{line.soFar}</div>
                        <div className="text-xs text-ink-800/70">{line.usual}</div>
                      </>
                    );
                  })()}
                </td>
                <td className="px-4 py-2 text-ink-800">
                  {(() => {
                    const line = activePlayersLine(room);
                    return (
                      <>
                        <div className="text-ink-950">{line.soFar}</div>
                        <div className="text-xs text-ink-800/70">{line.lastMonth}</div>
                      </>
                    );
                  })()}
                </td>
                <td className="px-4 py-2 text-ink-800">
                  {lastActivityText(room.lastOperatorActivity, overview.collectedSince)}
                </td>
                <td className="num px-4 py-2 text-right font-mono">{room.tournamentsAwaitingResults}</td>
              </tr>
            ))}
            {overview.rooms.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-xs text-ink-800/60">
                  No active rooms outside the internal ones.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function PlatformHealth({ overview }: { overview: AdminOverview }) {
  const { relay } = overview;
  return (
    <Section title="Platform health" intro="The activity system's own health. Errors and load live in Application Insights.">
      <div className="grid gap-4 md:grid-cols-2">
        <StatCard
          label="Activity waiting to be counted"
          value={relay.waiting.toLocaleString()}
          hint={
            relay.oldest
              ? `Oldest from ${new Date(relay.oldest).toLocaleString()}.`
              : 'Nothing is waiting.'
          }
        />
        <StatCard
          label="Set aside"
          value={relay.setAside.toLocaleString()}
          hint={
            relay.setAside === 0
              ? 'None — every record the relay took was counted.'
              : 'Records the relay gave up on. Somebody needs to read why and requeue them.'
          }
        />
        <div className="border border-line bg-surface p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">Errors and load</div>
          {overview.telemetryUrl ? (
            <a
              className="mt-1.5 inline-block text-sm font-medium text-ink-950 underline"
              href={overview.telemetryUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open Application Insights
            </a>
          ) : (
            <p className="mt-1.5 text-xs text-ink-800">No link is configured for this environment.</p>
          )}
        </div>
        <SignInRemovalsCard health={overview.signInRemovals} />
        <EngagementCard engagement={overview.engagement ?? null} />
        <WriterInstances writers={overview.writers} />
        <SessionsCard sessions={overview.sessions ?? null} />
      </div>
    </Section>
  );
}

/**
 * Erasures whose sign-in is still in the tenant — a promise of 30 days made to each player — flagged once any
 * is older than 25. Red is reserved for the flag: the count alone is ordinary work, not a fault.
 */
function SignInRemovalsCard({ health }: { health: AdminOverview['signInRemovals'] }) {
  const card = removalHealthCard(health);
  return (
    <div
      className="border border-line bg-surface p-4"
      style={card.flagged ? { borderTopWidth: 'var(--panel-rule)', borderTopColor: 'rgb(185 28 28)' } : undefined}
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">Sign-ins to remove</div>
      <div className="num mt-1.5 font-figure text-2xl font-bold leading-none text-ink-950">{card.value}</div>
      <p className={card.flagged ? 'mt-1.5 text-xs font-semibold text-red-700' : 'mt-1.5 text-xs text-ink-800'}>
        {card.hint}
      </p>
    </div>
  );
}

/**
 * "Usage events dropped": what the engagement writer lost over the last seven days, as a share of what
 * it took in — the measure of how far the engagement figures sit below the truth.
 */
function EngagementCard({ engagement }: { engagement: AdminOverview['engagement'] }) {
  const card = engagementCard(engagement ?? null);
  if (card.kind === 'not-collected') return <NotCollected title="Usage events dropped" says={card.says} />;

  return (
    <div className="border border-line bg-surface p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">
        Usage events dropped, last seven days
      </div>
      <div className="mt-1.5">
        <Ratio numerator={card.dropped} denominator={card.accepted} />
      </div>
      <p className="mt-1.5 text-xs text-ink-800">
        Usage figures are lower bounds; this is by how much.
      </p>
    </div>
  );
}

/**
 * Each engagement writer instance heard from this week, with when it last reported. A silent one —
 * three heartbeats without a report — says since when, and is the one line here that means something
 * has stopped. Not gated on collection: the writer runs whether or not anything records usage yet.
 */
function WriterInstances({ writers }: { writers: AdminOverview['writers'] }) {
  return (
    <div className="border border-line bg-surface p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">Usage writer</div>
      {writers.length === 0 ? (
        <p className="mt-1.5 text-xs text-ink-800">No instance has reported in the last seven days.</p>
      ) : (
        <ul className="mt-1.5 space-y-1 text-xs">
          {writers.map((w) => (
            <li key={w.instance} className="flex justify-between gap-3">
              <span className="truncate font-mono text-ink-800">{w.instance}</span>
              <span className={w.silent ? 'font-semibold text-ink-950' : 'text-ink-800'}>{writerLine(w)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * "Sessions from unrecognised apps": the week's session starts that carried a client id nothing in
 * configuration maps. Usually a missing id after an app registration changed; on a development
 * machine every session reads this way, since the bypass carries no client id.
 */
function SessionsCard({ sessions }: { sessions: AdminOverview['sessions'] }) {
  const card = sessionsCard(sessions ?? null);
  if (card.kind === 'not-collected') return <NotCollected title="Sessions from unrecognised apps" says={card.says} />;

  return (
    <div className="border border-line bg-surface p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800">
        Sessions from unrecognised apps, last seven days
      </div>
      <div className="mt-1.5">
        <Ratio numerator={card.unrecognised} denominator={card.sessions} />
      </div>
      <p className="mt-1.5 text-xs text-ink-800">
        A session from an app whose client id is not configured. Any here usually means a missing id.
      </p>
    </div>
  );
}
